import {
  BoxGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Mesh,
  MeshPhongMaterial,
  Object3D,
  OctahedronGeometry,
  PlaneGeometry,
  RingGeometry,
  SphereGeometry,
  TorusGeometry,
  TorusKnotGeometry
} from "three";

const GeometryType = {
  Box: "Box",
  Cone: "Cone",
  Cylinder: "Cylinder",
  Octahedron: "Octahedron",
  Plane: "Plane",
  Ring: "Ring",
  Sphere: "Sphere",
  Torus: "Torus",
  TorusKnot: "TorusKnot"
};

export default class PrimitiveShape extends Object3D {
  /**
   *
   */
  static geometryInstanceMap = new Map([
    [
      GeometryType.Box,
      p => new BoxGeometry(p.width, p.height, p.depth, p.widthSegments, p.heightSegments, p.depthSegments)
    ],
    [GeometryType.Cone, p => new ConeGeometry(p.radius, p.height, p.radialSegments, p.heightSegments)],
    [GeometryType.Sphere, p => new SphereGeometry(p.radius, p.widthSegments, p.heightSegments)],
    [
      GeometryType.Cylinder,
      p => new CylinderGeometry(p.radiusTop, p.radiusBottom, p.height, p.radiusSegments, p.heightSegments)
    ],
    [GeometryType.Plane, p => new PlaneGeometry(p.width, p.height, p.widthSegments, p.heightSegments)],
    [GeometryType.Octahedron, p => new OctahedronGeometry(p.radius, p.detail)],
    [GeometryType.Torus, p => new TorusGeometry(p.radius, p.tube, p.radialSegments, p.tubularSegments)],
    [
      GeometryType.TorusKnot,
      p => new TorusKnotGeometry(p.radius, p.tube, p.tubularSegments, p.radialSegments, p.p, p.q)
    ],
    [GeometryType.Ring, p => new RingGeometry(p.innerRadius, p.outerRadius, p.thetaSegments, p.phiSegments)]
  ]);

  static geometryParametersMap = new Map([
    [
      GeometryType.Box,
      {
        width: 2,
        height: 2,
        depth: 2,
        widthSegments: 1,
        heightSegments: 1,
        depthSegments: 1
      }
    ],
    [
      GeometryType.Cone,
      {
        radius: 1,
        height: 2,
        radialSegments: 16,
        heightSegments: 1
      }
    ],
    [
      GeometryType.Sphere,
      {
        radius: 1,
        widthSegments: 32,
        heightSegments: 16
      }
    ],
    [
      GeometryType.Cylinder,
      {
        radiusTop: 1,
        radiusBottom: 1,
        height: 2,
        radiusSegments: 32,
        heightSegments: 3
      }
    ],
    [
      GeometryType.Plane,
      {
        width: 2,
        height: 2,
        widthSegments: 1,
        heightSegments: 1
      }
    ],
    [
      GeometryType.Octahedron,
      {
        radius: 1,
        detail: 0
      }
    ],
    [
      GeometryType.Torus,
      {
        radius: 0.85,
        tube: 0.2,
        radialSegments: 16,
        tubularSegments: 32
      }
    ],
    [
      GeometryType.TorusKnot,
      {
        radius: 0.55,
        tube: 0.15,
        radialSegments: 16,
        tubularSegments: 64,
        p: 2,
        q: 3
      }
    ],
    [
      GeometryType.Ring,
      {
        innerRadius: 0.5,
        outerRadius: 1,
        thetaSegments: 32,
        phiSegments: 2
      }
    ]
  ]);

  constructor(color, geometryType, geometryParameters) {
    super();

    this.type = "PrimitiveShape";

    this._castShadow = false;
    this._receiveShadow = false;

    const materialColor = color || 0x00ff00;

    this._material = new MeshPhongMaterial({ side: THREE.DoubleSide });

    this.color = materialColor;

    this._geometryParameters =
      geometryParameters || PrimitiveShape.geometryParametersMap.get(geometryType || GeometryType.Box);

    const geometry = PrimitiveShape.geometryInstanceMap.get(geometryType || GeometryType.Box)(this._geometryParameters);

    this._mesh = new Mesh(geometry, this._material);
    this.add(this._mesh);
  }

  /**
   *
   */
  get color() {
    return this._color;
  }

  /**
   *
   */
  get geometryType() {
    return this._geometryType;
  }

  /**
   *
   */
  get geometryParameters() {
    return this._geometryParameters;
  }

  /**
   *
   */
  set color(value) {
    this._color = new Color(value);
    this._material.color = this._color;
    this._material.needsUpdate = true;
  }

  /**
   *
   */
  set geometryType(value) {
    this._geometryType = value;
    this._geometryParameters = PrimitiveShape.geometryParametersMap.get(value);
    this._build();
  }

  /**
   *
   */
  set geometryParameters(value) {
    this._geometryParameters = Object.assign(this._geometryParameters, value);
    this._build();
  }

  /**
   *
   */
  get castShadow() {
    return this._castShadow;
  }

  /**
   *
   */
  set castShadow(value) {
    this._castShadow = value;
    if (this._mesh) {
      this._mesh.castShadow = value;
      const { material } = this._mesh;
      if (material) {
        material.needsUpdate = true;
      }
    }
  }

  /**
   *
   */
  get receiveShadow() {
    return this._receiveShadow;
  }

  /**
   *
   */
  set receiveShadow(value) {
    this._receiveShadow = value;
    if (this._mesh) {
      this._mesh.receiveShadow = value;
      const { material } = this._mesh;
      if (material) {
        material.needsUpdate = true;
      }
    }
  }

  /**
   *
   */
  _build() {
    const prevGeometry = this._mesh.geometry;
    const geometry = PrimitiveShape.geometryInstanceMap.get(this.geometryType)(this.geometryParameters);
    prevGeometry.dispose();
    this._mesh.geometry = geometry;
    this._mesh.updateMatrix();
  }
}

AFRAME.registerComponent("primitive-shape", {
  schema: {
    color: { type: "string" },
    geometryType: { type: "string", default: "Box" },
    geometryParameters: { default: {} }
  },
  init() {
    this.el.emit("model-loading");

    this.shape = new PrimitiveShape(this.data.color, this.data.geometryType, this.data.geometryParameters);
    this.el.object3D.scriptableType = "primitive-shape";
    this.el.setObject3D("mesh", this.shape);

    this.el.emit("model-loaded", { format: "primitive-shape", model: this.shape });
  },
  update(oldData) {
    const data = this.data;

    if (data.color !== oldData.color) {
      this.shape.color = data.color || 0x00ff00;
    }

    if (data.geometryType !== oldData.geometryType) {
      this.shape.geometryType = data.geometryType;
    }

    if (JSON.stringify(data.geometryParameters) !== JSON.stringify(oldData.geometryParameters)) {
      this.shape.geometryParameters = data.geometryParameters;
    }
  }
});
